const AUDIO_MESSAGE_TYPES = new Set(['AUDIO', 'audio', 'VOICE', 'voice']);
const IMAGE_MESSAGE_TYPES = new Set(['IMAGE', 'image']);
const VIDEO_MESSAGE_TYPES = new Set(['VIDEO', 'video']);
const DOCUMENT_MESSAGE_TYPES = new Set(['DOCUMENT', 'document']);
const STICKER_MESSAGE_TYPES = new Set(['STICKER', 'sticker']);
const CONTACT_MESSAGE_TYPES = new Set(['CONTACTS', 'contacts']);

const normalizeReply = (message) => {
  const replyTo = message.reply_to ?? message.replyTo;

  if (!replyTo) {
    return null;
  }

  return {
    messageUuid: replyTo.message_uuid ?? replyTo.messageUuid ?? replyTo.id ?? replyTo.uuid ?? null,
    waMessageId: replyTo.wa_message_id ?? replyTo.waMessageId ?? null,
    text: replyTo.text ?? '',
    name: replyTo.name ?? '',
  };
};

const normalizeTranscription = (media) => {
  const transcription = media.transcription;

  if (!transcription) {
    return null;
  }

  // 'pending' | 'processing' | 'ready' | 'failed'
  return {
    text: transcription.text ?? '',
    status: transcription.status ?? null,
  };
};

const normalizeMedia = (message) => {
  const media = message.media;

  if (!media) {
    return null;
  }

  return {
    ...media,
    mimeType: media.mime_type ?? media.mimeType ?? null,
    caption: media.caption ?? '',
    filename: media.filename ?? '',
    isVoiceMessage: Boolean(media.is_voice_message ?? media.isVoiceMessage),
    duration: media.duration ?? null,
    signedUrl: media.signed_url ?? media.signedUrl ?? media.url ?? null,
    transcription: normalizeTranscription(media),
  };
};

const normalizeContact = (message) => {
  const contact = message.contact;

  if (!contact) {
    return null;
  }

  return {
    name: contact.name,
    phoneNumber: contact.phone_number,
    type: contact.type,
  }
}

const inferHasAudio = (messageType, media) => {
  if (AUDIO_MESSAGE_TYPES.has(messageType)) {
    return true;
  }

  if (media?.isVoiceMessage) {
    return true;
  }

  return Boolean(media?.mimeType?.startsWith('audio/'));
};

const inferMediaKind = (messageType, media, hasMedia) => {
  if (CONTACT_MESSAGE_TYPES.has(messageType)) {
    return 'contact';
  }

  if (!hasMedia && !media) {
    return 'text';
  }

  if (inferHasAudio(messageType, media)) {
    return 'audio';
  }

  if (STICKER_MESSAGE_TYPES.has(messageType)) {
    return 'sticker';
  }

  if (IMAGE_MESSAGE_TYPES.has(messageType)) {
    return 'image';
  }

  if (VIDEO_MESSAGE_TYPES.has(messageType)) {
    return 'video';
  }

  if (DOCUMENT_MESSAGE_TYPES.has(messageType)) {
    return 'document';
  }

  if (media?.mimeType?.startsWith('image/')) {
    return 'image';
  }

  if (media?.mimeType?.startsWith('video/')) {
    return 'video';
  }

  if (media?.mimeType?.startsWith('audio/')) {
    return 'audio';
  }

  return 'document';
};

const normalizeMessage = (message = {}) => {
  const media = normalizeMedia(message);
  const contact = normalizeContact(message);
  const isSent = Boolean(message.isSent ?? message.direction === 'OUT');
  const messageType = message.message_type ?? message.messageType ?? message.type ?? 'TEXT';
  const hasMedia = Boolean(message.has_media ?? message.hasMedia ?? media);
  const mediaKind = inferMediaKind(messageType, media, hasMedia);

  return {
    uuid: message.uuid ?? message.id ?? null,
    direction: message.direction ?? (isSent ? 'OUT' : 'IN'),
    isSent,
    messageType,
    hasMedia,
    mediaKind,
    text: message.text ?? '',
    sender: message.sender ?? null,
    senderName: message.sender?.name ?? message.senderName ?? null,
    eventAt: message.event_at ?? message.eventAt ?? message.fullTimestamp ?? null,
    timestamp: message.timestamp ?? '',
    pending: Boolean(message.pending),
    replyTo: normalizeReply(message),
    media,
    contact,
    hasAudio: mediaKind === 'audio',
    hasContact: mediaKind === 'contact',
  };
};

export default normalizeMessage;
